import Button from 'components/gruenland/atoms/button/Button';
import { Container } from 'components/gruenland/atoms/container/Container';
import { MarketContext } from 'context/market';
import useCloseFlyouts from 'helpers/hooks/useCloseFlyouts';
import { useFormat } from 'helpers/hooks/useFormat';
import useShopable from 'helpers/hooks/useShopable';
import { sendViewCartEvent } from 'helpers/utils/ga4';
import { useSearchParams } from 'next/navigation';
import { useContext, useEffect, useMemo } from 'react';
import CartSummary from '../cart-summary/CartSummary';
import CheckoutButton from '../cart-summary/CheckoutButton';
import { CheckoutButtonProps } from '../cart-summary/types';
import CartContent from './components/cart-content';
import InvalidCountryAlert from './components/InvalidCountryAlert';
import PaymentFailedAlert from './components/PaymentFailedAlert';
import ReservationFailedAlert from './components/ReservationFailedAlert';
import { CartProps } from './types';

const Cart = ({
  isEmpty,
  hasOutOfStockItems,
  onApplyDiscountCode,
  onRemoveDiscountCode,
  login,
  requestConfirmationEmail,
  requestPasswordReset,
  ...props
}: CartProps) => {
  const { formatMessage } = useFormat({ name: 'gruenland' });

  const closeFlyouts = useCloseFlyouts();
  const shopable = useShopable();
  const { openMarketDrawer } = useContext(MarketContext);

  const defaultCheckoutButtonProps: CheckoutButtonProps = useMemo(() => {
    return {
      text: formatMessage({ id: 'checkout.go', defaultMessage: 'Procedi al pagamento' }),
      onClick: closeFlyouts,
      disabled: isEmpty || hasOutOfStockItems,
    };
  }, [closeFlyouts, formatMessage, hasOutOfStockItems, isEmpty]);

  const summaryButton = shopable ? (
    <CheckoutButton className="mt-5" {...defaultCheckoutButtonProps} />
  ) : (
    <div className="mt-5">
      <Button variant="buttonSecondaryNegative" className="w-full" onClick={openMarketDrawer}>
        {formatMessage({ id: 'i18n.selectCountry', defaultMessage: 'Seleziona il paese' })}
      </Button>
    </div>
  );

  useEffect(() => {
    props.cart && sendViewCartEvent(props.cart);
  }, [props.cart]);

  const searchParams = useSearchParams();
  const reservationFailed = searchParams.get('error') === 'reservationFailed';
  const paymentFailed = searchParams.get('error') === 'paymentFailed';
  const invalidCountry = searchParams.get('error') === 'invalidCountry';

  return (
    <Container>
      {reservationFailed && <ReservationFailedAlert />}
      {paymentFailed && <PaymentFailedAlert />}
      {invalidCountry && <InvalidCountryAlert />}
      <div className="flex flex-col lg:flex-row lg:items-start lg:gap-20">
        <CartContent className="grow" isEmpty={isEmpty} {...props} />
        {!isEmpty && (
          <CartSummary
            className="lg:w-[30%]"
            button={summaryButton}
            discounts={props.cart?.discountCodes ?? []}
            onApplyDiscountCode={onApplyDiscountCode}
            onRemoveDiscountCode={onRemoveDiscountCode}
            login={login}
            requestConfirmationEmail={requestConfirmationEmail}
            requestPasswordReset={requestPasswordReset}
          />
        )}
      </div>
    </Container>
  );
};

export default Cart;
